import type { Order } from '../../services/api';
import type { ContractSelection } from './contractSelection';

export type ManualOrderPrefill = Partial<Order> & {
  con_id?: number;
  exchange?: string;
  multiplier?: string;
};

export interface ManualOrderPrefillState {
  symbol: string;
  secType: string;
  action: 'BUY' | 'SELL';
  strategyName: 'MANUAL' | 'NQMM';
  orderType: string;
  unsupportedOrderType: string | null;
  tif: 'DAY' | 'GTC';
  outsideRth: boolean;
  quantity: string;
  price: string;
  trailType: 'amount' | 'percent';
  trailingAmount: string;
  selectedContract: ContractSelection;
  error: string | null;
}

const ORDER_TYPE_ALIASES: Record<string, string> = {
  MKT: 'MARKET',
  MARKET: 'MARKET',
  LMT: 'LIMIT',
  LIMIT: 'LIMIT',
  MIDPRICE: 'MIDPRICE',
  STP: 'STOP',
  STOP: 'STOP',
  TRAIL: 'TRAIL',
};

export const buildManualOrderPrefillState = (
  order: ManualOrderPrefill,
  isEditing: boolean,
): ManualOrderPrefillState => {
  const rootSymbol = order.rootSymbol || order.symbol || '';
  const normalizedSymbol = rootSymbol.toUpperCase();
  const rawOrderType = String(order.order_type || '').toUpperCase();
  const orderType = ORDER_TYPE_ALIASES[rawOrderType] || 'MARKET';
  const unsupportedOrderType = orderType === 'MARKET' && !['MKT', 'MARKET'].includes(rawOrderType)
    ? rawOrderType || 'UNKNOWN'
    : null;
  const isTrailingPercent = order.trailing_percent !== undefined && order.trailing_percent !== null;
  const secType = String(order.secType || 'STK').toUpperCase();

  return {
    symbol: normalizedSymbol,
    secType,
    action: String(order.side || 'BUY').toUpperCase() === 'SELL' ? 'SELL' : 'BUY',
    strategyName: String(order.strategy || '').toUpperCase() === 'NQMM' ? 'NQMM' : 'MANUAL',
    orderType,
    unsupportedOrderType: isEditing ? unsupportedOrderType : null,
    tif: order.tif === 'GTC' ? 'GTC' : 'DAY',
    outsideRth: Boolean(order.outside_rth),
    quantity: String(order.quantity || 1),
    price: orderType === 'LIMIT' || orderType === 'MIDPRICE'
      ? String(order.limit_price ?? '')
      : orderType === 'STOP'
        ? String(order.stop_price ?? '')
        : '',
    trailType: isTrailingPercent ? 'percent' : 'amount',
    trailingAmount: orderType === 'TRAIL'
      ? String(isTrailingPercent ? order.trailing_percent ?? '' : order.trailing_amount ?? order.stop_price ?? '')
      : '',
    selectedContract: {
      symbol: normalizedSymbol,
      display_symbol: order.symbol ?? undefined,
      sec_type: secType,
      con_id: order.con_id,
      exchange: order.exchange,
      local_symbol: order.localSymbol,
      trading_class: order.tradingClass,
      last_trade_date_or_contract_month: order.lastTradeDateOrContractMonth,
      currency: order.currency ?? undefined,
      multiplier: order.multiplier,
    },
    error: isEditing && unsupportedOrderType
      ? `IB order type ${unsupportedOrderType} cannot be edited safely from this form.`
      : null,
  };
};
